import { createElement, type ReactElement } from 'react';
import { FileStack, FolderTree, ListOrdered, PencilLine, Scissors } from 'lucide-react';
import { findNativeQuickAction, type NativeQuickActionKey } from '@hector/shared-types/quick-actions';
import type { EmbeddedQuickAction } from './V3ChatInterface';

export type DossierQuickActionId = Extract<
  NativeQuickActionKey,
  'organize-pieces' | 'generate-bordereau-from-act' | 'rename-pieces' | 'renumber-pieces' | 'split-pdf'
>;

export type DossierQuickAction = EmbeddedQuickAction & {
  id: DossierQuickActionId;
};

/**
 * Ordre et icônes propres au dock dossier. Le texte (libellé, description,
 * prompt) vient du catalogue natif partagé (`@hector/shared-types/quick-actions`),
 * source unique aussi pour les actions natives de l'espace Assistant : une
 * intention ne peut plus dériver du prompt qu'elle sélectionne. Le dock
 * affiche toujours ses 5 actions dans cet ordre et ne lit JAMAIS l'ordre ou
 * le masquage choisis par l'avocat dans l'espace Assistant — ce sont deux
 * surfaces distinctes.
 */
const DOCK_ORDER: readonly { id: DossierQuickActionId; icon: ReactElement }[] = [
  { id: 'organize-pieces', icon: createElement(FolderTree, { className: 'h-4 w-4 text-blue-600' }) },
  { id: 'generate-bordereau-from-act', icon: createElement(FileStack, { className: 'h-4 w-4 text-amber-600' }) },
  { id: 'rename-pieces', icon: createElement(PencilLine, { className: 'h-4 w-4 text-emerald-600' }) },
  { id: 'renumber-pieces', icon: createElement(ListOrdered, { className: 'h-4 w-4 text-violet-600' }) },
  { id: 'split-pdf', icon: createElement(Scissors, { className: 'h-4 w-4 text-rose-600' }) },
];

export const DOSSIER_QUICK_ACTIONS: readonly DossierQuickAction[] = DOCK_ORDER.map(({ id, icon }) => {
  const native = findNativeQuickAction(id);
  if (!native) {
    throw new Error(
      `Dossier quick action "${id}" is missing from NATIVE_QUICK_ACTIONS (packages/shared-types/src/quick-actions). Restore the catalogue entry or remove it from DOCK_ORDER.`,
    );
  }
  return { id, label: native.title, description: native.description, prompt: native.prompt, icon };
});

export function getDossierQuickAction(id: DossierQuickActionId): DossierQuickAction {
  const action = DOSSIER_QUICK_ACTIONS.find((candidate) => candidate.id === id);
  if (!action) {
    throw new Error(
      `Unknown dossier quick action "${id}". Use an id from DOSSIER_QUICK_ACTIONS before opening the dossier assistant.`,
    );
  }
  return action;
}
